// Google Ads tag (gtag.js, AW-18537777436): GENERAL pages ONLY, conversion events with NO parameters.
//
// Privacy design:
// - ROUTE ALLOWLIST, not a blocklist. The tag loads, and conversions fire, only when the current
//   path is in ADS_TAG_ALLOWED_PATHS. Every other route (condition / biomarker / disease pages such as
//   /start (myeloma), /start/lung, /start/<cancer>, /clinical-trials/*, /es/ensayos-clinicos/*, the
//   patient dashboard, intake/upload, registration, results, anything with health data) never loads
//   gtag.js. A new page is tag-free until someone adds it here on purpose.
// - allow_ad_personalization_signals=false, allow_google_signals=false, Consent Mode
//   ad_personalization=denied: no remarketing / audience lists. No user_data is ever set, so no
//   enhanced conversions (also keep "Enhanced conversions" OFF in the Google Ads account settings,
//   because automatic enhanced conversions can read form fields).
// - page_location / page_referrer / page_title are overridden: origin + path only, plus gclid and
//   neutral utm_source/medium/campaign/content/id (values naming a condition/biomarker are dropped;
//   utm_term, the search keyword, is never sent). Every other query parameter and the hash are dropped. No page view is sent;
//   config only runs the conversion linker so the gclid can be attributed.
// - Conversions carry only send_to (no value, no transaction id, no custom params).
// - gclid is copied into first-party localStorage on ANY landing (no network call) for a later
//   server-side offline conversion upload that sends Google only gclid + conversion name + time.
// - Respects Global Privacy Control / Do Not Track: the tag never loads and gclid is not stored.
//   (The site has no cookie-consent banner today; see privacy-policy note in the PR.)
// - Static SEO pages (scripts/build-seo-pages.mjs) are separate HTML files and never import this.

export const GOOGLE_ADS_ID = 'AW-18537777436';

// ─── Conversion labels ─────────────────────────────────────────────────────────────────────────
// Google Ads conversion actions (Goals → Conversions, website, manual event snippet). The label is the
// part after "AW-18537777436/" in send_to. VITE_ env vars override the defaults without a code change;
// an override that is set but blank falls back to the default.
export const DEFAULT_ADS_CONVERSION_LABELS = Object.freeze({
  aria_call_request: 't_leCMSNkqsdEJySwIdF', // "CureGate – Aria call request" (Primary)
  quick_check_start: '5JXZCMeNkqsdEJySwIdF', // "CureGate – Quick check started" (Secondary)
});
export const ADS_CONVERSION_LABELS = Object.freeze({
  aria_call_request: String(import.meta.env?.VITE_GADS_LABEL_ARIA_CALL || '').trim() || DEFAULT_ADS_CONVERSION_LABELS.aria_call_request,
  quick_check_start: String(import.meta.env?.VITE_GADS_LABEL_QUICK_CHECK || '').trim() || DEFAULT_ADS_CONVERSION_LABELS.quick_check_start,
});
// ────────────────────────────────────────────────────────────────────────────────────────────────

// General, non-condition pages only. Exact matches; no prefixes, no wildcards.
export const ADS_TAG_ALLOWED_PATHS = Object.freeze([
  '/', // homepage
  // '/start/cancer' was removed (Oct 29, 2026): it is a condition page, so no Google tag or Meta pixel.
  // '/aria' is deliberately NOT here either: it is a health-topic page; gclid is still kept first-party.
  '/about-us-contact-us',
  '/refer',
  '/es/referir',
  '/privacy-policy',
  '/terms',
]);

const ALLOWED_PARAMS = /^(utm_(source|medium|campaign|content|id)|gclid)$/; // utm_term (search keyword) is never sent
// "Neutral" utm values only: a value naming a condition, biomarker or treatment is dropped.
const HEALTH_WORDS = /cancer|tumou?r|onc|lung|nsclc|sclc|myeloma|mm_|breast|colo|rect|prostat|pancrea|ovar|leuk|lymph|melanom|sarcom|brain|glio|kras|egfr|alk|ros1|braf|her2|pdl1|pd-l1|g12|biomark|chemo|immuno|diagnos|metasta|stage|mieloma|pulm|mama|ensayo|trial|disease|health|sick/i;
const GCLID_KEY = 'curegate-gclid';
const GCLID_TTL_MS = 90 * 24 * 60 * 60 * 1000; // Google Ads click-through window max.

export function normalizePath(pathname) {
  const p = String(pathname || '').split(/[?#]/)[0];
  if (!p.startsWith('/') || p.includes('//')) return '';
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
}

export function adsTagAllowedOnPath(pathname) {
  return ADS_TAG_ALLOWED_PATHS.includes(normalizePath(pathname));
}

/** origin + path, keeping only utm_* and gclid. Hash and every other parameter are dropped. */
export function sanitizePageLocation(href, origin = 'https://curegate.ai') {
  let url;
  try { url = new URL(href, origin); } catch { return `${origin}/`; }
  const out = new URL(normalizePath(url.pathname), url.origin);
  for (const [k, v] of url.searchParams) {
    if (!ALLOWED_PARAMS.test(k) || !/^[\w.~\-]{1,150}$/.test(v)) continue;
    if (k !== 'gclid' && HEALTH_WORDS.test(v)) continue;
    out.searchParams.append(k, v);
  }
  return out.toString();
}

/** Referrer reduced to its origin (no path, no query). */
export function sanitizeReferrer(ref) {
  try { return ref ? `${new URL(ref).origin}/` : ''; } catch { return ''; }
}

export function privacySignalOn(nav = typeof navigator !== 'undefined' ? navigator : null) {
  if (!nav) return false;
  return nav.globalPrivacyControl === true || nav.doNotTrack === '1' || nav.doNotTrack === 'yes';
}

export function isValidGclid(v) {
  return typeof v === 'string' && /^[\w-]{10,200}$/.test(v);
}

/** First-party gclid capture (localStorage). No network request. Returns the stored gclid or null. */
export function captureGclid(search, { storage = safeStorage(), now = Date.now(), nav } = {}) {
  if (!storage || privacySignalOn(nav)) return null;
  let gclid = null;
  try { gclid = new URLSearchParams(search || '').get('gclid'); } catch { /* ignore */ }
  if (!isValidGclid(gclid)) return readStoredGclid({ storage, now });
  try { storage.setItem(GCLID_KEY, JSON.stringify({ gclid, captured_at: new Date(now).toISOString() })); } catch { /* private mode */ }
  return gclid;
}

/** For the future server-side offline conversion upload: { gclid, captured_at } or null. */
export function readStoredGclid({ storage = safeStorage(), now = Date.now() } = {}) {
  try {
    const rec = JSON.parse(storage?.getItem(GCLID_KEY) || 'null');
    if (!rec || !isValidGclid(rec.gclid)) return null;
    if (now - Date.parse(rec.captured_at) > GCLID_TTL_MS) { storage.removeItem(GCLID_KEY); return null; }
    return rec.gclid;
  } catch { return null; }
}

function safeStorage() {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}

let loaded = false;
const fired = new Set();
let labels = ADS_CONVERSION_LABELS;

function gtag() { window.dataLayer.push(arguments); } // eslint-disable-line prefer-rest-params

function configure() {
  const loc = window.location;
  // 'set' applies to every later hit, including conversion events (whose tiba/url otherwise come
  // from document.title / location): the title is always "CureGate" and the URL stays sanitized.
  gtag('set', {
    page_location: sanitizePageLocation(loc.href, loc.origin),
    page_referrer: sanitizeReferrer(document.referrer),
    page_title: 'CureGate',
  });
  gtag('config', GOOGLE_ADS_ID, {
    send_page_view: false,
    allow_ad_personalization_signals: false,
    allow_google_signals: false,
    allow_enhanced_conversions: false, // no user_data / enhanced conversions, ever
    url_passthrough: false,
    page_location: sanitizePageLocation(loc.href, loc.origin),
    page_referrer: sanitizeReferrer(document.referrer),
    page_title: 'CureGate',
  });
}

/** Load gtag.js once, only on an allowlisted path and without a privacy signal. */
export function ensureAdsTag(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (!adsTagAllowedOnPath(pathname) || privacySignalOn()) return false;
  if (loaded) { configure(); return true; }
  window.dataLayer = window.dataLayer || [];
  gtag('consent', 'default', { ad_personalization: 'denied', ad_user_data: 'granted', ad_storage: 'granted', analytics_storage: 'denied' });
  gtag('set', 'allow_ad_personalization_signals', false);
  gtag('js', new Date());
  configure();
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(s);
  loaded = true;
  return true;
}

/** Route-change hook (Routes.jsx): store gclid first-party on any route; load the tag only where allowed. */
export function onAdsRouteChange(pathname, search) {
  captureGclid(search);
  return ensureAdsTag(pathname);
}

/**
 * Fire one conversion with send_to only. Once per page load per kind. No-op when the label is
 * empty, the path is not allowlisted, or a privacy signal is on.
 */
export function adsConversion(kind) {
  if (typeof window === 'undefined') return false;
  const label = labels[kind];
  if (!label || fired.has(kind)) return false;
  if (!ensureAdsTag(window.location.pathname)) return false;
  fired.add(kind);
  // Google's conversion hits read document.title directly (the config/set page_title does not reach
  // them), so the tab title is neutral ("CureGate") while the hit is built, then restored.
  const title = document.title;
  document.title = 'CureGate';
  gtag('event', 'conversion', { send_to: `${GOOGLE_ADS_ID}/${label}` });
  setTimeout(() => { if (document.title === 'CureGate') document.title = title; }, 3000);
  return true;
}

/** Call only AFTER a successful "Have Aria call me" request (RPC succeeded), never on the tap. */
export const adsAriaCallRequest = () => adsConversion('aria_call_request');
export const adsQuickCheckStart = () => adsConversion('quick_check_start');

// Test hook.
export function __resetAdsTagForTests(testLabels = ADS_CONVERSION_LABELS) { loaded = false; fired.clear(); labels = testLabels; }
